import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './App.css';

import ResidentForm from './components/ResidentForm';
import EditableResidentList from './components/EditableResidentList';
import ShiftSummary from './components/ShiftSummary';
import ExportButton from './components/ExportButton';
import { generateCallSchedule } from './utils/scheduler';

function App() {
  const [residents, setResidents] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [startDate, setStartDate] = useState(() => {
    const today = DateTime.now().startOf('day');
    return today.toJSDate();
  });
  const [endDate, setEndDate] = useState(() => {
    const today = DateTime.now().startOf('day');
    return today.toJSDate();
  });
  const [isSelectingEndDate, setIsSelectingEndDate] = useState(false);
  const [schedulingPreference, setSchedulingPreference] = useState('minimize_weekend');
  const [warning, setWarning] = useState('');
  const [isScheduleGenerated, setIsScheduleGenerated] = useState(false);
  const [schedulerMode, setSchedulerMode] = useState('resident');
  const [specialShifts, setSpecialShifts] = useState({});
  const [handoverDay, setHandoverDay] = useState(1);
  const [warnings, setWarnings] = useState([]);

  const addResident = (resident) => {
    setResidents([...residents, resident]);
    setWarning('');
  };

  const removeResident = (index) => {
    const newResidents = [...residents];
    newResidents.splice(index, 1);
    setResidents(newResidents);
  };

  const updateResident = (index, updatedResident) => {
    const newResidents = [...residents];
    newResidents[index] = updatedResident;
    setResidents(newResidents);
  };

  const clearRequests = () => {
    setResidents([]);
    setSchedule({});
    setWarning('');
  };

  const generateSchedule = () => {
    if (residents.length < 2) {
      setWarning('Please add at least two names before generating the schedule.');
    } else {
      const adjustedStartDate = DateTime.fromJSDate(startDate);
      const adjustedEndDate = DateTime.fromJSDate(endDate);
      
      const { schedule, totalShifts, specialShifts, warnings } = generateCallSchedule(
        adjustedStartDate.toJSDate(),
        adjustedEndDate.toJSDate(),
        residents,
        schedulingPreference,
        schedulerMode,
        handoverDay,
      );
      setSchedule(schedule);
      setSpecialShifts(specialShifts);
      setResidents(prevResidents => {
        const updatedResidents = prevResidents.map(resident => ({
          ...resident,
          totalShifts: totalShifts[resident.name] || 0
        }));
        return updatedResidents;
      });
      setWarnings(warnings);
      setIsScheduleGenerated(true);
    }
  };

  const handleDateChange = (date, isStart) => {
    if (isStart) {
      setStartDate(date);
      if (date > endDate) {
        setEndDate(date);
      }
    } else {
      if (date < startDate) {
        setStartDate(date);
      }
      setEndDate(date);
    }
  };

  const handleDateClick = (date) => {
    if (!isSelectingEndDate) {
      setStartDate(date);
      setEndDate(date);
      setIsSelectingEndDate(true);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
      setIsSelectingEndDate(false);
    }
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dateStr = date.toISOString().split('T')[0];
      const classes = [];

      if (dateStr === startDate.toISOString().split('T')[0]) {
        classes.push('react-calendar__tile--rangeStart');
      }
      if (dateStr === endDate.toISOString().split('T')[0]) {
        classes.push('react-calendar__tile--rangeEnd');
      }
      if (date > startDate && date < endDate) {
        classes.push('react-calendar__tile--rangeBetween');
      }

      // Highlight empty spots
      if (schedule[dateStr] === null) {
        classes.push('empty-spot');
      }

      // Highlight back-to-back shifts for residents
      if (schedulerMode === 'resident') {
        const prevDateStr = DateTime.fromJSDate(date).minus({ days: 1 }).toISODate();
        if (schedule[dateStr] && schedule[dateStr] === schedule[prevDateStr]) {
          classes.push('back-to-back');
        }
      }

      // For attending mode, highlight the entire week
      if (schedulerMode === 'attending') {
        const weekStart = DateTime.fromJSDate(date).startOf('week').plus({ days: handoverDay - 1 });
        const weekEnd = weekStart.plus({ days: 6 });
        if (schedule[weekStart.toISODate()] === schedule[weekEnd.toISODate()]) {
          classes.push(schedule[weekStart.toISODate()]);
        }
      }

      return classes.join(' ');
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="App">
      <header className="header">
        <h1>On Call Scheduler</h1>
      </header>
      <div className="container">
        <div className="schedule-section">
          <h2>Schedule</h2>
          <div className="mode-selector">
            <label>Scheduler Mode:</label>
            <select 
              value={schedulerMode} 
              onChange={(e) => setSchedulerMode(e.target.value)}
            >
              <option value="resident">Resident</option>
              <option value="attending">Attending</option>
            </select>
          </div>
          {schedulerMode === 'attending' && (
            <div className="handover-day-selector">
              <label>Handover Day:</label>
              <select 
                value={handoverDay}
                onChange={(e) => setHandoverDay(parseInt(e.target.value))}
              >
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
                <option value="7">Sunday</option>
              </select>
            </div>
          )}
          <div className="date-picker">
            <label>First day:</label>
            <input 
              type="date" 
              value={startDate.toISOString().substr(0, 10)} 
              onChange={(e) => handleDateChange(new Date(e.target.value), true)} 
            />
          </div>
          <div className="date-picker">
            <label>Last day:</label>
            <input 
              type="date" 
              value={endDate.toISOString().substr(0, 10)} 
              onChange={(e) => handleDateChange(new Date(e.target.value), false)} 
            />
          </div>
          {/* {schedulerMode === 'resident' && (
            <div className="preference-input">
              <label>Scheduling Preference:</label>
              <select value={schedulingPreference} onChange={(e) => setSchedulingPreference(e.target.value)}>
                <option value="minimize_weekend">Minimize Fri, Sat, Sun</option>
                <option value="balance_week">Balance Across Week</option>
              </select>
            </div>
          )} */}
          <div className="calendar-container">
            <Calendar
              value={[startDate, endDate]}
              onChange={handleDateClick}
              selectRange={false}
              onClickDay={handleDateClick}
              tileContent={({ date, view }) => {
                if (view === 'month') {
                  const dateStr = date.toISOString().split('T')[0];
                  return <p>{schedule[dateStr]}</p>;
                }
              }}
              tileClassName={tileClassName}
            />
          </div>
          {warnings.length > 0 && (
            <div className="warnings">
              <h3>Warnings:</h3>
              <ul>
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
            )}
          </div>
        <div className="call-roster-section">
        <div className="form-container">
          <h2>Call Roster</h2>
          <p>The scheduling algorithm will select the optimal distribution of names based on your preferences. If there are not enough people to satisfy the 1 in X requirement, the least optimal dates will be empty. For details on how the scheduling algorithm works, please click <a href="algorithm.pdf" target="_blank" rel="noreferrer">here</a>.</p>
          <ResidentForm addResident={addResident} schedulerMode={schedulerMode} />
          <EditableResidentList 
            residents={residents}
            removeResident={removeResident}
            updateResident={updateResident}
            schedulerMode={schedulerMode}
          />
          <div className="call-roster-buttons">
            <button
              className="generate-button"
              onClick={generateSchedule}
            >
              Create Schedule
            </button>
            <button className="clear-button" onClick={clearRequests}>Clear requests</button>
          </div>
          {warning && <p className="warning">{warning}</p>}
        </div>
      </div>
      </div>
      <div className="summary-section">
        <ShiftSummary 
          schedule={schedule} 
          residents={residents} 
          startDate={startDate} 
          endDate={endDate}
          schedulerMode={schedulerMode}
          specialShifts={specialShifts}
          handoverDay={handoverDay}
        />
        <ExportButton
          schedule={schedule} 
          residents={residents}
          startDate={startDate}
          endDate={endDate}
          isScheduleGenerated={isScheduleGenerated}
          schedulerMode={schedulerMode}
          specialShifts={specialShifts}
          handoverDay={handoverDay}
        />
      </div>
      <div className="copyright">
        © <a className="link" href="https://eddieguo.ca/" target="_blank" rel="noreferrer">Eddie Guo</a> 2024-{currentYear}
      </div>
    </div>
  );
}

export default App;